export const defaultPwaServiceWorkerPath = '/sw.js';

export function createMinimalServiceWorkerSource(appScope: string): string {
  return `const APP_SCOPE = ${JSON.stringify(appScope)};
const CACHE_VERSION = 'pwa-app-shell-v1';
const STATIC_CACHE_NAME = CACHE_VERSION + ':static';
const APP_SHELL_CACHE_NAME = CACHE_VERSION + ':shell';
const NORMALIZED_SCOPE = APP_SCOPE.endsWith('/') ? APP_SCOPE : APP_SCOPE + '/';
const APP_SHELL_URL = new URL(NORMALIZED_SCOPE, self.location.origin).toString();
const PRECACHE_URLS = [
  APP_SHELL_URL,
  new URL('/manifest.webmanifest', self.location.origin).toString(),
  new URL('/favicon.ico', self.location.origin).toString(),
  new URL('/favicon-16x16.png', self.location.origin).toString(),
  new URL('/favicon-32x32.png', self.location.origin).toString(),
  new URL('/apple-touch-icon.png', self.location.origin).toString(),
  new URL('/pwa/icon-192x192.png', self.location.origin).toString(),
  new URL('/pwa/icon-512x512.png', self.location.origin).toString(),
  new URL('/pwa/icon-192x192-maskable.png', self.location.origin).toString(),
  new URL('/pwa/icon-512x512-maskable.png', self.location.origin).toString(),
];

function isSameOrigin(requestUrl) {
  return requestUrl.origin === self.location.origin;
}

function isAppScopePath(pathname) {
  return pathname === APP_SCOPE || pathname.startsWith(NORMALIZED_SCOPE);
}

function isApiOrMutableRequest(requestUrl, request) {
  if (request.method !== 'GET') {
    return true;
  }

  return (
    requestUrl.pathname.startsWith('/api/') ||
    requestUrl.pathname.startsWith('/v1/') ||
    requestUrl.pathname.startsWith('/auth/') ||
    requestUrl.pathname.startsWith('/socket') ||
    requestUrl.pathname.includes('/socket.io/') ||
    requestUrl.pathname.includes('/realtime') ||
    requestUrl.pathname.includes('/uploads/') ||
    requestUrl.searchParams.has('X-Amz-Signature') ||
    requestUrl.searchParams.has('x-amz-signature') ||
    requestUrl.searchParams.has('signature')
  );
}

function isStaticAssetRequest(requestUrl, request) {
  if (request.method !== 'GET') {
    return false;
  }

  if (
    request.destination === 'script' ||
    request.destination === 'style' ||
    request.destination === 'font' ||
    request.destination === 'image' ||
    request.destination === 'manifest'
  ) {
    return true;
  }

  return /\\.(?:js|mjs|css|woff2?|ttf|otf|png|jpe?g|webp|avif|svg|ico|webmanifest)$/i.test(
    requestUrl.pathname,
  );
}

async function cacheFirst(request) {
  const cached = await caches.match(request);

  if (cached) {
    return cached;
  }

  const response = await fetch(request);

  if (response.ok) {
    const cache = await caches.open(STATIC_CACHE_NAME);
    await cache.put(request, response.clone());
  }

  return response;
}

async function navigationFallback(request) {
  try {
    const response = await fetch(request);

    if (response.ok) {
      const cache = await caches.open(APP_SHELL_CACHE_NAME);
      await cache.put(APP_SHELL_URL, response.clone());
    }

    return response;
  } catch (error) {
    const cached = await caches.match(APP_SHELL_URL);

    if (cached) {
      return cached;
    }

    throw error;
  }
}

self.addEventListener('install', (event) => {
  self.skipWaiting();
  event.waitUntil(
    caches.open(APP_SHELL_CACHE_NAME).then((cache) =>
      Promise.allSettled(
        PRECACHE_URLS.map((url) =>
          cache.add(new Request(url, { cache: 'reload' })),
        ),
      ),
    ),
  );
});

self.addEventListener('activate', (event) => {
  event.waitUntil(
    caches
      .keys()
      .then((cacheNames) =>
        Promise.all(
          cacheNames
            .filter(
              (cacheName) =>
                cacheName.startsWith('pwa-app-shell-') &&
                cacheName !== STATIC_CACHE_NAME &&
                cacheName !== APP_SHELL_CACHE_NAME,
            )
            .map((cacheName) => caches.delete(cacheName)),
        ),
      )
      .then(() => self.clients.claim()),
  );
});

self.addEventListener('message', (event) => {
  if (event.data && event.data.type === 'SKIP_WAITING') {
    self.skipWaiting();
  }
});

self.addEventListener('fetch', (event) => {
  const requestUrl = new URL(event.request.url);

  if (!isSameOrigin(requestUrl)) {
    return;
  }

  // Phase 1 intentionally excludes API/auth/upload/realtime responses from
  // caching. Those responses can contain actor-scoped private data or signed
  // upload URLs, and this phase only guarantees offline app-shell rendering.
  if (isApiOrMutableRequest(requestUrl, event.request)) {
    return;
  }

  if (event.request.mode === 'navigate' && isAppScopePath(requestUrl.pathname)) {
    event.respondWith(navigationFallback(event.request));
    return;
  }

  // Static assets are cache-first after the app has seen them once. sw.js is
  // excluded so browser update checks can always discover a new worker version.
  if (requestUrl.pathname === '/sw.js') {
    return;
  }

  if (isStaticAssetRequest(requestUrl, event.request)) {
    event.respondWith(cacheFirst(event.request));
  }
});
`;
}
